import { useState } from "react";

import { CloseIcon, MoreIcon } from "@/assets/icons";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger } from "@/components/ui/popover";
import { useAnalysisSummaryDataContext } from "@/contexts/AnalysisSummaryDataContext";

import { PopoverContent, PopoverPortal } from "@radix-ui/react-popover";

import SortAndFilterSection from "./sortAndFilter";
import TransformationMethodSection from "./transformationMethod";
import { AggregationPopoverProps, AnalysisConfiguration } from "./types";
import { getDatasetSummarySortByOptions } from "./utils";

export {
  AGGREGATION_CALCULATION_OPTIONS,
  AGGREGATION_CALCULATION_PIVOT_OPTIONS,
  CATEGORY_OPTIONS,
  METHOD_OPTIONS,
  SORT_BY_OPTIONS,
  SORT_OPTIONS
} from "./constants";
export { ECategoryTransform, TransformationMethod } from "./types";

const AggregationPopover = ({
  dataset,
  isOpen,
  analysisConfiguration,
  attributesOptions,
  gridsSelected,
  setAnalysisConfiguration,
  onOpenChange
}: AggregationPopoverProps) => {
  const { gridValue } = useAnalysisSummaryDataContext();

  // This is the only state for user editing
  const [analysisConfigurationTemp, setAnalysisConfigurationTemp] =
    useState<AnalysisConfiguration | null>(analysisConfiguration);

  const handleCancel = () => {
    setAnalysisConfigurationTemp(analysisConfiguration);
    onOpenChange(false);
  };

  const handleApply = () => {
    const nextSortByOptions = getDatasetSummarySortByOptions(
      analysisConfigurationTemp,
      gridsSelected,
      gridValue
    );
    const isSortByValid = nextSortByOptions.some(
      (opt) => opt.value === analysisConfigurationTemp?.sortBy?.value
    );

    setAnalysisConfiguration(
      isSortByValid
        ? analysisConfigurationTemp
        : { ...analysisConfigurationTemp, sortBy: nextSortByOptions[0] }
    );
    onOpenChange(false);
  };

  const handleOpen = () => {
    setAnalysisConfigurationTemp(analysisConfiguration);
    onOpenChange(true);
  };

  const handlePopoverOpenChange = (open: boolean) =>
    open ? handleOpen() : handleCancel();

  return (
    <Popover onOpenChange={handlePopoverOpenChange} open={isOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="p-2 rounded-full hover:bg-gray-100 print:hidden"
        >
          <MoreIcon className="h-8 w-8 fill-txtBody" />
        </button>
      </PopoverTrigger>
      <PopoverPortal>
        <PopoverContent
          className="p-3 mb-4 rounded-xl w-[260px] z-[999] bg-white shadow-[0px_4px_24px_5px_#CECECE66]"
          side="top"
          align="end"
          onInteractOutside={(event) => event.preventDefault()}
          hideWhenDetached
        >
          <div className="flex flex-col gap-4">
            <div className="flex justify-between items-center">
              <span className="text-sm font-semibold text-txtHeading">
                Configuration
              </span>
              <Button
                className="w-fit p-0 bg-white size-auto [&_svg]:size-6"
                aria-label="Close"
                onClick={handleCancel}
              >
                <CloseIcon className="fill-dim-gray" />
              </Button>
            </div>
            <div className="flex flex-col gap-3">
              <span className="text-xs font-semibold text-txtPlaceholder">
                Transformation Method
              </span>
              <TransformationMethodSection
                attributesOptions={attributesOptions}
                analysisConfiguration={analysisConfigurationTemp}
                setAnalysisConfiguration={setAnalysisConfigurationTemp}
              />
            </div>
            <div className="flex flex-col gap-3">
              <span className="text-xs font-semibold text-txtPlaceholder">
                Sort and Filter
              </span>
              <SortAndFilterSection
                dataset={dataset}
                analysisConfiguration={analysisConfigurationTemp}
                setAnalysisConfiguration={setAnalysisConfigurationTemp}
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="w-full rounded-xl h-8"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button className="w-full rounded-xl h-8" onClick={handleApply}>
                Apply
              </Button>
            </div>
          </div>
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  );
};

export default AggregationPopover;
